import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { StatelessPopupProps } from 'components/popup/stateless_popup/types';
import { useKeyPress } from 'custom_hooks/useKeyPress';
import { forwardRef, Ref, RefObject } from 'react';
import ReactFocusLock from 'react-focus-lock';

export type ActionMenuProps<TriggerElement extends HTMLElement = HTMLButtonElement> =
    Omit<StatelessPopupProps, 'noOverlay'> &
        Readonly<{
            /**
             * The button/trigger element that this action menu should be anchored to.
             */
            buttonRef: RefObject<TriggerElement>;
            /**
             * Uses the popup overlay to prevent click propagation when clicking outside of the popup.
             * Defaults to `false`.
             */
            useOverlay?: boolean;
        }>;

export const ActionMenu = forwardRef(
    (
        {
            ariaLabel = 'Action Menu',
            children,
            isFullscreen = false, // ActionMenu defaults to never go to full screen
            buttonRef,
            useOverlay = false,
            ...props
        }: ActionMenuProps,
        ref: Ref<HTMLDivElement>,
    ) => {
        useKeyPress('Escape', () => {
            props.onClose();
            buttonRef.current?.focus();
        });
        return (
            <StatelessPopup
                {...props}
                ref={ref}
                aria-label={ariaLabel}
                noPadding
                noOverlay={!useOverlay}
                isFullscreen={isFullscreen}
                anchor={buttonRef.current}
            >
                <ReactFocusLock>{children}</ReactFocusLock>
            </StatelessPopup>
        );
    },
);
